import type { Contact, Lead, OfferVatMode, Organization, TaxTreatment } from '../../../types';
import { contactDisplayName } from '../../../types';
import { isInvoiceFamilyDocumentType } from '../../../utils/documentNumberFormat';
import { DEFAULT_OFFER_VAT_MODE, isOfferVatMode } from './offerVat';
import {
  formatLeadPartyName,
  formatRecipientForOffer,
  splitAddressBlock,
  type RecipientLayout,
} from './offerDocumentUtils';

export type InvoiceDebtorSource = 'project_customer' | 'recipient_contact' | 'recipient_organization';

export type InvoiceDebtor = {
  source: InvoiceDebtorSource;
  nameLines: string[];
  streetLine: string;
  plzOrtLine: string;
  partyName: string;
  email: string;
  cc: string[];
  taxTreatment: TaxTreatment;
  vatId: string;
  commercialRegisterNumber: string;
  recipientOrganizationId: string | null;
  recipientContactId: string | null;
  isBlocked: boolean;
  blockReason: string;
  warningText: string;
};

export type DocumentDraftBilling = {
  documentType?: string | null;
  recipientNameLines?: string[] | null;
  recipientStreetLine?: string | null;
  recipientPlzOrtLine?: string | null;
  vat_mode?: string | null;
};

export type DocumentBilling = {
  debtor: InvoiceDebtor;
  layout: RecipientLayout;
  partyName: string;
  /** Gesetzt, wenn die Rechnung an einen abweichenden Empfänger geht. */
  email: string | undefined;
  cc: string[] | undefined;
  vatMode: OfferVatMode;
};

function normalizeTax(value: unknown): TaxTreatment {
  return value === 'reverse_charge_13b' ? 'reverse_charge_13b' : 'standard';
}

function emailList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.map((item) => String(item ?? '').trim()).filter(Boolean);
}

function layoutFrom(name: string, address: string): RecipientLayout {
  const addr = splitAddressBlock(address);
  return {
    nameLines: name.trim() ? [name.trim()] : [],
    streetLine: addr.street,
    plzOrtLine: addr.plzOrt,
  };
}

function projectParty(lead: Lead): Contact | Organization | null {
  if (lead.organization) return lead.organization;
  return lead.contact ?? null;
}

export function resolveInvoiceDebtor(lead: Lead): InvoiceDebtor {
  const party = projectParty(lead);
  const offerLayout = formatRecipientForOffer(lead);
  const projectOrg = lead.organization ?? null;
  const base: InvoiceDebtor = {
    source: 'project_customer',
    nameLines: offerLayout.nameLines,
    streetLine: offerLayout.streetLine,
    plzOrtLine: offerLayout.plzOrtLine,
    partyName: formatLeadPartyName(lead),
    email: '',
    cc: emailList(projectOrg?.billing_email_cc),
    taxTreatment: normalizeTax(party?.tax_treatment),
    vatId: (projectOrg?.vat_id ?? '').trim(),
    commercialRegisterNumber: (projectOrg?.commercial_register_number ?? '').trim(),
    recipientOrganizationId: null,
    recipientContactId: null,
    isBlocked: Boolean(party?.is_blocked),
    blockReason: String(party?.block_reason ?? '').trim(),
    warningText: String(party?.warning_text ?? '').trim(),
  };

  const recipientOrg = party?.invoice_recipient_organization ?? null;
  if (party?.invoice_recipient_organization_id && recipientOrg) {
    const billing = recipientOrg.billing_contact ?? null;
    const address = (billing?.address ?? '').trim() || (recipientOrg.registered_address ?? '').trim();
    const name = (recipientOrg.name ?? '').trim();
    const layout = layoutFrom(name, address);
    return {
      ...base,
      source: 'recipient_organization',
      nameLines: layout.nameLines,
      streetLine: layout.streetLine,
      plzOrtLine: layout.plzOrtLine,
      partyName: name || base.partyName,
      email: (billing?.email ?? '').trim(),
      cc: emailList(recipientOrg.billing_email_cc),
      taxTreatment: normalizeTax(recipientOrg.tax_treatment),
      vatId: (recipientOrg.vat_id ?? '').trim(),
      commercialRegisterNumber: (recipientOrg.commercial_register_number ?? '').trim(),
      recipientOrganizationId: String(recipientOrg.id),
      recipientContactId: null,
    };
  }

  const recipientContact = party?.invoice_recipient_contact ?? null;
  if (party?.invoice_recipient_contact_id && recipientContact) {
    const name = contactDisplayName(recipientContact);
    const layout = layoutFrom(name, recipientContact.address ?? '');
    return {
      ...base,
      source: 'recipient_contact',
      nameLines: layout.nameLines,
      streetLine: layout.streetLine,
      plzOrtLine: layout.plzOrtLine,
      partyName: name || base.partyName,
      email: (recipientContact.email ?? '').trim(),
      cc: [],
      taxTreatment: normalizeTax(recipientContact.tax_treatment),
      vatId: '',
      commercialRegisterNumber: '',
      recipientOrganizationId: null,
      recipientContactId: String(recipientContact.id),
    };
  }

  return base;
}

/**
 * Neue Rechnungen an einen gesetzten Empfänger nutzen dessen Name, Adresse,
 * E-Mail und Steuerkennzeichen. Angebote bleiben beim Projektkunden.
 * Ein gespeicherter Entwurf desselben Belegtyps behält seine Empfängerzeilen
 * und seinen Steuermodus.
 */
export function resolveDocumentBilling(
  lead: Lead,
  documentTypeLabel: string,
  draft: DocumentDraftBilling | null | undefined,
): DocumentBilling {
  const debtor = resolveInvoiceDebtor(lead);
  const invoice = isInvoiceFamilyDocumentType(documentTypeLabel);
  const draftType = String(draft?.documentType ?? '').trim() || 'Angebot';
  const draftMatches = draftType === String(documentTypeLabel ?? '').trim();
  const designated = invoice && debtor.source !== 'project_customer';
  const offerLayout = formatRecipientForOffer(lead);
  const defaults: RecipientLayout = designated
    ? {
        nameLines: debtor.nameLines,
        streetLine: debtor.streetLine,
        plzOrtLine: debtor.plzOrtLine,
      }
    : offerLayout;
  const layout: RecipientLayout =
    designated && !draftMatches
      ? defaults
      : {
          nameLines:
            draft?.recipientNameLines && draft.recipientNameLines.length > 0
              ? draft.recipientNameLines
              : defaults.nameLines,
          streetLine: draft?.recipientStreetLine ?? defaults.streetLine,
          plzOrtLine: draft?.recipientPlzOrtLine ?? defaults.plzOrtLine,
        };

  let vatMode: OfferVatMode = isOfferVatMode(draft?.vat_mode) ? draft.vat_mode : DEFAULT_OFFER_VAT_MODE;
  if (invoice && !draftMatches && debtor.taxTreatment === 'reverse_charge_13b') {
    vatMode = 'vat_0_reverse_charge';
  }

  return {
    debtor,
    layout,
    partyName: designated
      ? layout.nameLines[0]?.trim() || debtor.partyName
      : formatLeadPartyName(lead),
    email: designated ? debtor.email : undefined,
    cc: designated ? debtor.cc : undefined,
    vatMode,
  };
}
